import React from 'react';
import CursorCoordinatesItem from './CursorCoordinatesItem';
import type { CursorCoordinatesItemProps } from './CursorCoordinatesItem';
import QACheckItem from './QACheckItem';
import type { QACheckItemProps } from './QACheckItem';

interface OverlayInstance {
  SeriesDescription?: string;
  StudyDate?: string;
  Modality?: string;
  ReferringPhysicianName?: string;
}

export const VIEWPORT_OVERLAY_TOP_LEFT = [
  {
    id: 'SeriesDescription',
    inheritsFrom: 'ohif.overlayItem',
    label: '',
    title: 'Series description',
    condition: ({
      instance,
      referenceInstance,
    }: {
      instance?: OverlayInstance;
      referenceInstance?: OverlayInstance;
    }) => instance?.SeriesDescription || referenceInstance?.SeriesDescription,
    contentF: ({
      instance,
      referenceInstance,
    }: {
      instance?: OverlayInstance;
      referenceInstance?: OverlayInstance;
    }) => instance?.SeriesDescription || referenceInstance?.SeriesDescription || '',
  },
];

export const VIEWPORT_OVERLAY_BOTTOM_LEFT = [
  {
    id: 'CursorCoordinates',
    inheritsFrom: 'sycai.overlayItem.cursorCoordinates',
    contentF: (props: CursorCoordinatesItemProps) => <CursorCoordinatesItem {...props} />,
  },
  {
    id: 'WindowLevel',
    inheritsFrom: 'ohif.overlayItem.windowLevel',
    title: 'Window Level',
  },
  {
    id: 'ZoomLevel',
    inheritsFrom: 'ohif.overlayItem.zoomLevel',
    condition: (props: {
      toolGroupService: { getActiveToolForViewport: (viewportId: string) => string };
      viewportId: string;
    }) => props.toolGroupService.getActiveToolForViewport(props.viewportId) === 'Zoom',
  },
  {
    id: 'QACheck',
    inheritsFrom: 'sycai.overlayItem.qaCheck',
    contentF: (props: QACheckItemProps) => <QACheckItem {...props} />,
  },
  {
    id: 'ReferringPhysicianName',
    inheritsFrom: 'ohif.overlayItem',
    label: 'Referring Physician:',
    title: 'Referring Physician Name',
    condition: ({ instance }: { instance?: OverlayInstance }) =>
      instance?.Modality === 'CT' && instance?.ReferringPhysicianName,
    contentF: ({
      instance,
      formatters: { formatPN },
    }: {
      instance: OverlayInstance;
      formatters: { formatPN: (name?: string) => string | undefined };
    }) => formatPN(instance.ReferringPhysicianName),
  },
  {
    id: 'StudyDate',
    inheritsFrom: 'ohif.overlayItem',
    label: 'Study Date:',
    title: 'Study date',
    condition: ({
      instance,
      referenceInstance,
    }: {
      instance?: OverlayInstance;
      referenceInstance?: OverlayInstance;
    }) => instance?.StudyDate || referenceInstance?.StudyDate,
    contentF: ({
      instance,
      referenceInstance,
      formatters: { formatDate },
    }: {
      instance?: OverlayInstance;
      referenceInstance?: OverlayInstance;
      formatters: { formatDate: (date: string) => string };
    }) => {
      const studyDate = instance?.StudyDate || referenceInstance?.StudyDate;
      return studyDate ? formatDate(studyDate) : '';
    },
  },
];
